import { TabPanel, Tabs, Typography } from '@getgo/chameleon-web-react-wrapper/register';
import { ChangeEvent, Fragment, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useJobDeviceUpdates } from '../../hooks/use-update-job-device-updates.hook';
import { TabsComponent } from '@getgo/chameleon-web';
import useActiveAndPastUpdatesLink from '../active-and-past-updates/useActiveAndPastUpdatesLink';
import useUpdateDetailsLink from '../update-details/useUpdateDetailsLink';
import ContentHeader from '../../../core/components/ContentHeader';
import JobDeviceOsUpdatesGrid from './JobDeviceOsUpdatesGrid';
import JobDeviceAppUpdatesGrid from './JobDeviceAppUpdatesGrid';
import { TrackedTab } from '../../../core/components/tracking/TrackedComponents';
import { JobDeviceUpdateOverviewTabSwitchedEvent } from '../../../core/models/UserTrackingEvents';
import { upperCaseFirstLetter } from '../../../core/utilities/utilities';
import clsx from 'clsx';
import styles from './JobDeviceUpdates.module.scss';
import { useAppSelector } from '../../../core/store/root.store';
import { selectNavigationRoute, selectPartiallySuccessfulHistoryEnabled } from '../../../ui-frame/state/ui-frame.slice';
import { navigate } from '../../../core/services/navigation.service';
import { TabDefinition } from '../../../patch-view/types/frontend/tabs-definition';
import useGetTenantDevicesAndGroups from '../../hooks/useGetTenantDevicesAndGroups';
import { useUpdateJobs } from '../../hooks/use-update-jobs-hook';

export type JobHistoryOverviewTabs = 'Operating system updates' | 'Application updates';

export default function JobDeviceUpdates(): ReactElement {
  const params = useParams();
  const { t } = useTranslation();
  const jobId = params.jobId as string;
  const navigationRoute = useAppSelector(selectNavigationRoute);
  const partiallySuccessfulHistoryEnabled = useAppSelector(selectPartiallySuccessfulHistoryEnabled);
  const { isLoading, allUpdates, jobName, deviceName } = useJobDeviceUpdates(jobId, params.deviceId as string);
  const { tenantDevices } = useGetTenantDevicesAndGroups();
  const { loading: allJobsLoading, jobs: allJobs } = useUpdateJobs(tenantDevices);

  const activeAndPastUpdatesLink = useActiveAndPastUpdatesLink();
  const updateDetailsLink = useUpdateDetailsLink(jobId, jobName);

  const [hideStatusColumn, setHideStatusColumn] = useState<boolean>(false);

  useEffect(() => {
    if (!allJobsLoading && allJobs.length) {
      const currentJob = allJobs.find(({ id }) => id === jobId);
      const updateInfo = currentJob?.osUpdateInfo || currentJob?.appUpdateInfo;
      if (updateInfo) {
        setHideStatusColumn(!partiallySuccessfulHistoryEnabled || updateInfo.version < 2);
      }
    }
  }, [jobId, allJobs, allJobsLoading, partiallySuccessfulHistoryEnabled]);

  const TABS: TabDefinition<JobHistoryOverviewTabs>[] = [
    {
      tabId: 'Operating system updates',
      locationSuffix: '/os-jobs-history',
      component: (
        <JobDeviceOsUpdatesGrid
          updates={allUpdates.osUpdates}
          isLoading={isLoading}
          hideStatusColumn={hideStatusColumn}
        />
      )
    },
    {
      tabId: 'Application updates',
      locationSuffix: '/app-jobs-history',
      component: (
        <JobDeviceAppUpdatesGrid
          updates={allUpdates.appUpdates}
          isLoading={isLoading}
          hideStatusColumn={hideStatusColumn}
        />
      )
    }
  ];

  const activeTabId = ['/os-jobs-history', '/app-jobs-history'].find(locationSuffix => {
    return navigationRoute.includes(locationSuffix);
  });

  const onTabChange = ({ currentTarget }: ChangeEvent<TabsComponent>): void => {
    if (currentTarget.activetab.id === activeTabId) {
      return;
    }
    navigate({
      path: `/updates/:jobId/devices/:deviceId/updates/:activeTab`,
      params: { jobId, deviceId: params.deviceId as string, activeTab: currentTarget.activetab.id }
    });
  };

  return (
    <>
      <ContentHeader
        title={
          <Typography variant="heading-medium" tag="h1">
            {deviceName && t('deviceDetails.title', { device: deviceName })}
          </Typography>
        }
        breadcrumbLinks={[activeAndPastUpdatesLink, updateDetailsLink]}
      />
      <Tabs className={styles.tabs} activeid={activeTabId} label="Tabs label" onChange={onTabChange}>
        {TABS.map(tab => (
          <Fragment key={tab.tabId}>
            <TrackedTab
              trackingEvent={new JobDeviceUpdateOverviewTabSwitchedEvent(tab.tabId)}
              key={tab.tabId}
              id={tab.locationSuffix}>
              {upperCaseFirstLetter(tab.tabId)}
            </TrackedTab>
            <TabPanel className={clsx(styles.deviceUpdatesContainer)} id={tab.tabId}>
              {tab.component}
            </TabPanel>
          </Fragment>
        ))}
      </Tabs>
    </>
  );
}
